import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { ReportTypeWithContext } from '../Api/CustomerApiRequestsAndResponses';
import { Certification } from '../Certifications';
import { FileTableEntry } from '../Documents/DocumentModels';
import { SoldItem } from '../SoldItems';
import { DynamicRegulation } from "../Compliance/DynamicRegulation";

export type SharedMaterialIdToCoreDataTable = { [sharedMaterialId: string]: SharedMaterialCoreData };

export enum SharedMaterialFmdDiscloureType {
    Ppm100 = "Ppm100",
    Ppm1000 = "Ppm1000",
    PerGhsSds = "PerGhsSds",
    PerOshaMsds = "PerOshaMsds",
    Other = "Other"
}

export function localizeSharedMaterialFmdDisclosureType(type: SharedMaterialFmdDiscloureType,
    localizer: IStringLocalizer): string {
    switch (type) {
        case SharedMaterialFmdDiscloureType.Ppm1000: return "1000 ppm";
        case SharedMaterialFmdDiscloureType.Ppm100: return "100 ppm";
        case SharedMaterialFmdDiscloureType.PerGhsSds: return "Per GHS SDS";
        case SharedMaterialFmdDiscloureType.PerOshaMsds: return "Per OSHA MSDS";
        default: return localizer.localizeString("Other");
    }
}

export enum SharedMaterialAccessType {
    Private = "Private",
    Protected = "Protected",
    Public = "Public"
}

export enum StandardComplianceScoreType {
    Unknown = "Unknown",
    Compliant = "Compliant",
    NonCompliant = "NonCompliant"
}

export enum StandardComplianceScoreAsNumber {
    Unknown,
    Compliant,
    NonCompliant
}

export interface SharedMaterialsAccessRequest {
    ownerId: string;
    requestId: string;
    requestorTeamOwnerId: string;
    requestorUserId: string;
    sharedMaterialIds: string[];
    dateSent: string //ISO8061 format.
}

export interface SharedMaterialsAccessRequestCatalogRequest extends SharedMaterialsAccessRequest {
    requestorName: string;
    requestorEmail: string;
}

export interface SharedMaterialsAccessRequestCatalog {
    accessRequests: SharedMaterialsAccessRequestCatalogRequest[];
    allSharedMaterialsCoreDataMap: { [sharedMaterialIds: string]: SharedMaterialCoreData };
}

export enum AccessRequestResponseType {
    Approved,
    Declined
}

export interface CompanyInfo {
    description: string;
    websiteUrl: string;
}

interface BaseSharedMaterialsCompany {
    ownerId: string;
    name: string;
    logoFileId: string | null;
    contactEmail: string;
}

export interface SharedMaterialsCatalogCompany extends BaseSharedMaterialsCompany {
    isUnClaimed?: boolean;
    companyId: string;
}

export interface SharedMaterialsFullCompany extends SharedMaterialsCatalogCompany {
    companyInfo: CompanyInfo | null;
    customUrlName: string | null;
    reports: string[];
}

export interface SharedMaterialsEditableCompany extends BaseSharedMaterialsCompany {
    companyId: string | null; // null means the company doesn't exist yet
    companyInfo: CompanyInfo | null;
    customUrlName: string | null;
    reports: string[];
    localModelId: string;
}

export interface CustomReportTypesInfo {
    customReportTypes: string[];
    overallReportId: string;
}

export interface SharedMaterialCoreData {
    ownerId: string;
    sharedMaterialId: string;
    implementationInventoryId: string;
    publicInventoryId: string;
    publicInventoryDataId: string;
    accessLevel: SharedMaterialAccessType;
    hasAccess: boolean;
    materialName: string;
    companyName: string;
    materialPhotoFileId: string | null;
    sku: string;
    categories: string[];
    regulationIdToComplianceScoreMap: { [regulationId: string]: StandardComplianceScoreType };
    scoreRohs: StandardComplianceScoreType | null;
    scoreCal65: StandardComplianceScoreType | null;
    scoreCm: StandardComplianceScoreType | null;
    scoreCmrtV2: StandardComplianceScoreType | null;
    scoreReach: StandardComplianceScoreType | null;
    scoreReachAuth: StandardComplianceScoreType | null;
    scoreReachAuthV2: StandardComplianceScoreType | null;
    fmdStatus: SharedMaterialFmdDiscloureType | null;
    hasSdsStatus: boolean | null;
    lastModified: string; // ISO8601 date-time string
    fetchForCatalogs: boolean;
    companyId: string;
}

export interface SharedMaterialsCatalogData {
    materials: SharedMaterialCoreData[];
    categories: string[];
    dynamicRegulationTable: { [regulationId: string]: DynamicRegulation };
    totalNumberOfMaterials: number;
}

export enum SharedMaterialInventoryType {
    RawMaterial = "RawMaterial",
    Article = "Article"
}

interface SearchResult {
    substanceId: string;
    privateSubstanceId: string;
    casNumber: string;
    chemicalName: string;
    alsoCalled: string;
    overallScore: string;
    biodegradabilityStatus: string;
    ecNumbers: string[];
}

export interface SubstanceResult {
    result: string;
    resultsList: SearchResult[];
    pageNum: number;
    pageStartRowIndex: number;
    totalNumRows: number;
    numPagesOfToxnotSubstanceData: number;
    numPagesOfPrivateSubstanceData: number;
    totalNumPages: number;
}

export interface SharedMaterialGeneralData {
    description: string;
    idOrPartNum: string;
    uuid: string;
    countryOfOrigin: string;
    qrCodeFileId: string | null;
    regionalAvailability: string[];
    purchaseUrl: string;
    inventoryType: SharedMaterialInventoryType;
}

export enum SharedMaterialScope {
    ProductStage = "ProductStage",
    ConstructionStage = "ConstructionStage",
    UseStage = "UseStage",
    EndOfLifeStage = "EndOfLifeStage",
    ReuseRecoveryRecyclingPotential = "ReuseRecoveryRecyclingPotential"
}

export function getFriendlyNameFromScope(localizer: IStringLocalizer, type: SharedMaterialScope) {
    const map = {
        [SharedMaterialScope.ProductStage]: localizer.localizeString("Product Stage (Cradle-to-Gate) A1-A3"),
        [SharedMaterialScope.ConstructionStage]: localizer.localizeString("Construction Stage A4-A5"),
        [SharedMaterialScope.UseStage]: localizer.localizeString("Use Stage B1-B5"),
        [SharedMaterialScope.EndOfLifeStage]: localizer.localizeString("End of Life Stage C1-D4"),
        [SharedMaterialScope.ReuseRecoveryRecyclingPotential]: localizer.localizeString("Reuse/Recovery/Recycling Potential D"),
    };

    return map[type] ?? "?";
}

export interface SharedMaterialSustainabilityDataTransparency { }

export interface SharedMaterialAllSustainabilityDataAssessmentCommitmentOptimization { }

export interface SharedMaterialAllSustainabilityData {
    embodiedCarbon: SharedMaterialSustainabilityData | null;
    waterUse: SharedMaterialSustainabilityData | null;
    transparency: SharedMaterialSustainabilityDataTransparency | null;
    assessmentCommitmentOptimization: SharedMaterialAllSustainabilityDataAssessmentCommitmentOptimization | null;
}

export interface SharedMaterialSustainabilityData {
    amount: string;
    productUnit: string;
    scope: SharedMaterialScope[];
    supportingDocumentFileIds: string[];
}

export enum SharedMaterialVocTestingType {
    Content = "Content",
    Emissions = "Emissions",
    FormaldehydeEmissions = "FormaldehydeEmissions",
    InherentlyNonEmitting = "InherentlyNonEmitting"
}

export function getFriendlyNameForVocTestingType(localizer: IStringLocalizer, type: SharedMaterialVocTestingType) {
    const map = {
        [SharedMaterialVocTestingType.Content]: localizer.localizeString("VOC Content"),
        [SharedMaterialVocTestingType.Emissions]: localizer.localizeString("VOC Emissions"),
        [SharedMaterialVocTestingType.FormaldehydeEmissions]: localizer.localizeString("Formaldehyde Emissions"),
        [SharedMaterialVocTestingType.InherentlyNonEmitting]: localizer.localizeString("Inherently Non-Emitting"),
    };

    return map[type] ?? "";
}

export interface SharedMaterialVocDataTransparency {
    testingTypes: SharedMaterialVocTestingType[];
    vocContent: string;
}

export interface SharedMaterialVocDataAssessmentCommitmentOptimization { }

export interface SharedMaterialVocData {
    transparency: SharedMaterialVocDataTransparency | null;
    assessmentCommitmentOptimization: SharedMaterialVocDataAssessmentCommitmentOptimization | null;
}

export interface SharedMaterialInstructionsInstallation {
    documentFileIds: string[];
    notes: string;
}

export interface SharedMaterialInstructionsUsePhase {
    lifeExpectancy: string;
    documentFileIds: string[];
    warrantyNotes: string;
}

export interface SharedMaterialInstructionsSpecifications {
    specifications: string[][];
}

export interface SharedMaterialInstructions {
    installation: SharedMaterialInstructionsInstallation | null;
    usePhase: SharedMaterialInstructionsUsePhase | null;
    specifications: SharedMaterialInstructionsSpecifications | null;
}

export enum LbcRedListStatus {
    RedListFree = "RedListFree",
    RedListApproved = "RedListApproved",
    Declared = "Declared",
    Unknown = "Unknown"
}

export enum SharedMaterialPackaging {
    Undefined = "Undefined",
    Included = "Included",
    Excluded = "Excluded"
}

export enum SharedMaterialEolOptionType {
    DesignedForMaintenanceAndRepair = "DesignedForMaintenanceAndRepair",
    DesignedForDisassembly = "DesignedForDisassembly",
    DesignedForReUse = "DesignedForReUse",
    TakeBackProgram = "TakeBackProgram",
    Recycling = "Recycling",
    Other = "Other"
}

export interface SharedMaterialEolOption {
    type: SharedMaterialEolOptionType,
    description: string,
    attachmentFileIds: string[],
    supportingLink: string
}

export interface SharedMaterialEolOption_DesignedForMaintenanceAndRepair extends SharedMaterialEolOption {
    maintainedByUntrained: boolean,
    maintainedByTrained: boolean,
    maintenanceNotRequired: boolean,
    maintenanceNotPossible: boolean,
    consumableReplacedByUntrained: boolean,
    sparePartsAvailable: boolean
}

export interface SharedMaterialEolOption_DesignedForDisassembly extends SharedMaterialEolOption {
    instructions: string
}

export interface SharedMaterialEolOption_DesignedForReuse extends SharedMaterialEolOption {
    minimalModification: boolean,
    ceMark: boolean,
    refurbishment: boolean,
    remanufacturing: boolean,
    dedicatedCollectionSystem: boolean,
    emitted: boolean,
    industrialCascading: boolean,
    industrialComposter: boolean,
    homeComposter: boolean,
    biodegestion: boolean,
    incineration: boolean,
    recyclingPercent: string,
    leakagePercent: string
}

export interface SharedMaterialEolOption_TakeBackProgram extends SharedMaterialEolOption {
    instructions: string
}

export interface SharedMaterialEolOption_Recycling extends SharedMaterialEolOption {
    instructions: string
}

export interface SharedMaterialEolOption_Other extends SharedMaterialEolOption {
    name: string
}

export interface SharedMaterialCircularityData {
    packaging: SharedMaterialPackaging;
    packagingInformation: string;
    eolContact: string;
    eolOptions: SharedMaterialEolOption[];
    rapidlyRenewableContentRollUpPercent: string,
    recycledContentRollUpPercent: string;
    preConsumerRecyclableContentRollUpPercent: string;
    postConsumerRecyclableContentRollUpPercent: string;
    productCircularityDataSheetFileId: string | null;
}

export interface SharedMaterialBomData {
    disclosureThreshold: string;
}

export interface SharedMaterialComplianceDetailData {
    hasScipData: boolean;

}export interface SdsDetailData {
    hasSdsFields: boolean;
}

export interface SharedMaterialReport {
    reportType: ReportTypeWithContext,
    fileId: string;
}

export interface SharedMaterialDetailRestrictedData {
    bomData: SharedMaterialBomData | null;
    complianceDetail: SharedMaterialComplianceDetailData;
    sustainability: SharedMaterialAllSustainabilityData | null;
    circularity: SharedMaterialCircularityData | null;
    documentFileIds: string[];
    reports: SharedMaterialReport[];
    soldItems: SoldItem[] | null;
    certifications: Certification[] | null;
    vocData: SharedMaterialVocData | null;
    instructions: SharedMaterialInstructions | null;
    lbcRedListStatus: LbcRedListStatus | null;
    sdsData: SdsDetailData;
}

export interface SharedMaterialDetailData {
    sharedMaterialId: string;
    general: SharedMaterialGeneralData;
    restrictedData: SharedMaterialDetailRestrictedData | null;
}

export interface SharedMaterialDetailResponse {
    detailData: SharedMaterialDetailData;
    fileTable: { [fileId: string]: FileTableEntry };
    libraryInventoryId: string | null;
    isUnClaimed: boolean;
    dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>;
    company: SharedMaterialsCatalogCompany;
}

export interface SharedMaterial {
    coreData: SharedMaterialCoreData;
    detailData: SharedMaterialDetailData;
}

export interface SharedMaterialsAccessControlListInfo {
    ownerId: string;
    teamOwnerIdWithAccess: string;
    sharedMaterialId: string;
    userWithAccessName: string;
    userWithAccessEmail: string;
}

export interface AccessManagerBundle {
    accessControlListMap: { [sharedMaterialId: string]: SharedMaterialsAccessControlListInfo[] },
    numberOfActiveAccessRequests: number;
}

export interface UploadResult {
    result: string;
    importId: string;
}

export interface OfflineResult {
    result: string;
    import: ImportResult;
}

export interface ImportResult {
    state: StateImportResult;
}

export interface StateImportResult {
    currentProgressItemIndex: number;
    progressTotalNumItems: number;
    processingStage: string;
}

export enum ProcessingStage {
    Queued = "Queued",
    UploadingInitialSpreadsheet = "UploadingInitialSpreadsheet",
    UploadingSdsFiles = "UploadingSdsFiles",
    ImportingSpreadsheetData = "ImportingSpreadsheetData",
    ImportingSpreadsheetLibrary = "ImportingSpreadsheetLibrary",
    ExtractingAndLinking = "ExtractingAndLinking",
    ProcessingSdsFiles = "ProcessingSdsFiles",
    WritingSuppliers = "WritingSuppliers",
    CompleteAndNeedsQa = "CompleteAndNeedsQa",
    TotallyComplete = "TotallyComplete",
    Failed = "Failed",
    WritingMyGreenLabProducts = "WritingMyGreenLabProducts",
    GeneratingMyGreenLabLabels = "GeneratingMyGreenLabLabels",
    Publishing = "Publishing",
    InventoryLibraryReadingMaterials = "InventoryLibraryReadingMaterials",
    InventoryLibraryWritingMaterials = "InventoryLibraryWritingMaterials",
    LookingUpSubstances = "LookingUpSubstances",
    InventoryLibraryWritingBomRowData = "InventoryLibraryWritingBomRowData",
    InventoryLibraryComputingComplianceScores = "InventoryLibraryComputingComplianceScores",
    InventoryLibraryWritingSummaryData = "InventoryLibraryWritingSummaryData",
    InventorySummaryDataLibraryProcessingRows = "InventorySummaryDataLibraryProcessingRows",
    InventorySummaryDataLibraryWritingSummaryData = "InventorySummaryDataLibraryWritingSummaryData",
    InventorySummaryDataLibraryComputingComplianceScores = "InventorySummaryDataLibraryComputingComplianceScores"
}
